import { useState, useEffect } from 'react'
import { UserAPI } from '@certi/shared/api/user/UserAPI'
import type { UserProfileResponse } from '@certi/shared/types'
import { useAuth } from '@/hooks/useAuth'
import ProfileForm from '@/components/profile/ProfileForm'
import SecuritySection from '@/components/profile/SecuritySection'

export default function ProfilePage() {
  const { user, session } = useAuth()
  const [profile, setProfile] = useState<UserProfileResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'profile' | 'security'>('profile')

  useEffect(() => {
    if (!user?.id || !session?.accessToken) return

    const fetchProfile = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await UserAPI.getUserProfile(user.id, session.accessToken)
        setProfile(response)
      } catch (err) {
        console.error('Failed to fetch profile:', err)
        setError(err instanceof Error ? err.message : 'Failed to load profile')
      } finally {
        setLoading(false)
      }
    }

    fetchProfile()
  }, [user?.id, session?.accessToken])

  const handleProfileUpdate = (updatedProfile: UserProfileResponse) => {
    setProfile(updatedProfile)
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-8"></div>
          <div className="space-y-6">
            <div className="h-6 bg-gray-200 rounded w-1/4"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !profile) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Error Loading Profile
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="btn btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Account Settings</h1>
        <p className="text-gray-600">Manage your profile and account security</p>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('profile')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'profile'
                ? 'border-brand-primary text-brand-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Profile Information
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'security'
                ? 'border-brand-primary text-brand-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Security & Privacy
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white">
        {activeTab === 'profile' && (
          <ProfileForm 
            profile={profile} 
            onUpdate={handleProfileUpdate}
          />
        )}
        {activeTab === 'security' && (
          <SecuritySection 
            profile={profile} 
            onUpdate={handleProfileUpdate}
          />
        )}
      </div>
    </div>
  )
}